/**
 * Native push for the store app — the phone's own notification channel.
 *
 * In a browser or the Home Screen web app, pushes arrive through the service
 * worker (pwa/pushClient.js). Inside the App Store build there is no service
 * worker: iOS hands the app an APNs device token, the app sends it to the CRM
 * (POST /push/device) and the server delivers through Apple directly. This
 * module is that path. pushClient.js delegates here whenever the CRM runs
 * inside the shell, so the Settings toggle, the first-open sheet, logout and
 * the login-time reconnect all work the same on both channels.
 *
 * iOS only today: Android is not distributed through a store (Android
 * phones install the web app), and the server speaks APNs alone. Every
 * function is a no-op (or "unsupported") elsewhere, so callers need no guard.
 *
 * The plugins are reached through the core bridge (registerPlugin, like
 * biometric.js and badge.js), made on first use inside native-only code so
 * the web bundle registers nothing.
 *
 * NEVER hand a plugin proxy back through a promise — no `await plugin`, no
 * `async () => plugin`, no `.then((m) => m.PushNotifications)`. The proxy
 * answers every property with a method wrapper, `then` included, so a
 * promise resolved with it takes it for a thenable, calls that wrapper, and
 * waits forever for callbacks that never come. That was the 2026-10-23
 * store-app bug: "loading the notifications plugin" stalled for the life
 * of the page (Settings card, first-open sheet, boot warm-up, listeners).
 * Hold the proxy in a plain variable and await only its method calls.
 */
import { Capacitor, registerPlugin } from "@capacitor/core";
import { PushPermissionError } from "../pwa/pushErrors";
import { createLogger, summarizeError } from "../utils/logger";

const log = createLogger("native.push");

const API_BASE = process.env.REACT_APP_BASE_URL || "";
const buildUrl = (path) => {
  if (!API_BASE) return path;
  return API_BASE.endsWith("/") ? `${API_BASE.slice(0, -1)}${path}` : `${API_BASE}${path}`;
};

// Where the registered token is kept so logout can tell the server to forget
// this phone, and Settings can show "Enabled on this device" without asking
// iOS to register again.
const TOKEN_PREF_KEY = "push.deviceToken";

// iOS answers registerForRemoteNotifications within a second or two. A
// build that cannot register at all — the AppDelegate hooks or the push
// entitlement missing, i.e. a store build older than 1.1.0 — never answers.
const REGISTRATION_TIMEOUT_MS = 15_000;

export const UPDATE_APP_LINE =
  "Notifications need a newer version of the Phieg app. Update it from the App Store, then try again.";

export const isNativeApp = () => Capacitor.isNativePlatform();

/** True inside the iOS store app, where this channel exists. */
export const isNativePushSupported = () =>
  Capacitor.isNativePlatform() && Capacitor.getPlatform() === "ios";

// The browser exposes Notification.permission synchronously; the plugin only
// asynchronously. The last answer is cached so pushClient.getPermission() can
// stay synchronous for the callers that render from it; the async
// getPermissionAsync() is what decides anything.
let cachedPermission = "default";
export const getCachedNativePermission = () => cachedPermission;

const toWebPermission = (state) => {
  if (state === "granted") return "granted";
  if (state === "denied") return "denied";
  return "default"; // "prompt" | "prompt-with-rationale" | unknown
};

// One proxy per plugin, made on first use (native paths only — see the
// header). Synchronous on purpose: see the header's warning.
const proxies = {};
const plugin = (name) => {
  if (!proxies[name]) proxies[name] = registerPlugin(name);
  return proxies[name];
};
const pushPlugin = () => plugin("PushNotifications");
const preferencesPlugin = () => plugin("Preferences");

// A native call that never answers — the plugin missing on the native side,
// a bridge queue that is stuck — leaves its promise pending forever:
// Capacitor neither resolves nor rejects it. Every read the Settings card
// and the switch depend on is bounded, and a stall names its step so the
// card can say which one (seen 2026-10-23 on the first 1.1.0 install:
// "Checking current status…" forever).
export const NATIVE_CALL_TIMEOUT_MS = 8000;

const STAGE_WORDS = {
  plugin_load: "loading the notifications plugin",
  check_permissions: "reading the notification permission",
  saved_token: "reading the saved device token",
  app_info: "reading the app version",
};

/** The step in plain words, for the card and the switch's error line. */
export const describeNativeStage = (stage) => STAGE_WORDS[stage] || stage;

export class NativeStallError extends Error {
  constructor(stage) {
    super(
      `The phone did not answer while ${describeNativeStage(stage)}. Close Phieg completely (swipe it away in the app switcher), open it again and retry.`
    );
    this.name = "NativeStallError";
    this.stage = stage;
  }
}

/** Reject with NativeStallError(stage) when `promise` has not settled in time. */
export const withNativeTimeout = (promise, stage, ms = NATIVE_CALL_TIMEOUT_MS) =>
  new Promise((resolve, reject) => {
    const timer = setTimeout(() => reject(new NativeStallError(stage)), ms);
    Promise.resolve(promise).then(
      (value) => {
        clearTimeout(timer);
        resolve(value);
      },
      (error) => {
        clearTimeout(timer);
        reject(error);
      }
    );
  });

// The permission as iOS reports it right now. Throws — a NativeStallError
// when a step never answers — so probeNativePushStatus() can say which.
async function readPermission() {
  const PushNotifications = pushPlugin();
  const { receive } = await withNativeTimeout(
    PushNotifications.checkPermissions(),
    "check_permissions"
  );
  cachedPermission = toWebPermission(receive);
  return cachedPermission;
}

/**
 * "default" | "granted" | "denied" | "unsupported" — never prompts, never
 * throws: a failed or stalled read answers with the last known value.
 */
export async function getNativePushPermission() {
  if (!isNativePushSupported()) return "unsupported";
  try {
    await readPermission();
  } catch (error) {
    log.error("push.permission_check_failed", {
      stage: error instanceof NativeStallError ? error.stage : null,
      ...summarizeError(error),
    });
  }
  return cachedPermission;
}

/** Warm the cached permission at boot; errors are already logged. */
export const primeNativePushPermission = () =>
  getNativePushPermission().catch(() => {});

// The saved token, or null. Throws (NativeStallError on a step that never
// answers) — readSavedToken() is the forgiving face.
async function readSavedTokenRaw() {
  const Preferences = preferencesPlugin();
  const { value } = await withNativeTimeout(
    Preferences.get({ key: TOKEN_PREF_KEY }),
    "saved_token"
  );
  return value || null;
}

async function readSavedToken() {
  try {
    return await readSavedTokenRaw();
  } catch (error) {
    log.error("push.token_read_failed", {
      stage: error instanceof NativeStallError ? error.stage : null,
      ...summarizeError(error),
    });
    return null;
  }
}

async function saveToken(token) {
  try {
    const Preferences = preferencesPlugin();
    await Preferences.set({ key: TOKEN_PREF_KEY, value: token });
  } catch (error) {
    log.error("push.token_save_failed", { ...summarizeError(error) });
  }
}

async function clearSavedToken() {
  try {
    const Preferences = preferencesPlugin();
    await Preferences.remove({ key: TOKEN_PREF_KEY });
  } catch (error) {
    log.error("push.token_clear_failed", { ...summarizeError(error) });
  }
}

/** The saved device token, or null when this phone is not registered. */
export async function getNativeDeviceToken() {
  if (!isNativePushSupported()) return null;
  return readSavedToken();
}

/**
 * Is this phone registered right now: permission granted AND a token the
 * server was given. Mirrors isCurrentlySubscribed() on the web.
 */
export async function isNativePushRegistered() {
  if (!isNativePushSupported()) return false;
  if ((await getNativePushPermission()) !== "granted") return false;
  return Boolean(await readSavedToken());
}

/**
 * What the Settings card renders from: the permission, whether this phone
 * is registered, and — when a native step never answered within
 * NATIVE_CALL_TIMEOUT_MS — which one (`stalledAt`), so the card can say so
 * instead of spinning forever. Never throws.
 */
export async function probeNativePushStatus() {
  if (!isNativePushSupported()) {
    return { permission: "unsupported", registered: false, stalledAt: null };
  }
  const answer = (registered, stalledAt = null) => ({
    permission: cachedPermission,
    registered,
    stalledAt,
  });
  const stalled = (error) => {
    log.error("push.status_stalled", { stage: error.stage });
    return answer(false, error.stage);
  };
  try {
    await readPermission();
  } catch (error) {
    if (error instanceof NativeStallError) return stalled(error);
    log.error("push.permission_check_failed", { ...summarizeError(error) });
    return answer(false);
  }
  if (cachedPermission !== "granted") return answer(false);
  try {
    return answer(Boolean(await readSavedTokenRaw()));
  } catch (error) {
    if (error instanceof NativeStallError) return stalled(error);
    log.error("push.token_read_failed", { ...summarizeError(error) });
    return answer(false);
  }
}

const describeRegistrationError = (error) => {
  const message = String(error?.message || error || "").trim();
  // "no valid 'aps-environment' entitlement string found" — the build has
  // no push entitlement, which only a store update fixes.
  if (/aps-environment/i.test(message)) return UPDATE_APP_LINE;
  return `This phone could not be registered for notifications${
    message ? `: ${message}` : "."
  }`;
};

// Ask iOS for the device token. Listeners go on BEFORE register(): the
// plugin fires `registration` as soon as iOS answers, and an answer with
// nobody listening is lost.
async function obtainDeviceToken(PushNotifications) {
  const handles = [];
  let settle = null;
  const tokenPromise = new Promise((resolve, reject) => {
    settle = { resolve, reject };
  });

  handles.push(
    await PushNotifications.addListener("registration", ({ value }) => {
      if (value) settle.resolve(String(value));
      else settle.reject(new Error(describeRegistrationError("empty token")));
    })
  );
  handles.push(
    await PushNotifications.addListener("registrationError", ({ error }) => {
      settle.reject(new Error(describeRegistrationError(error)));
    })
  );
  const timer = setTimeout(
    () => settle.reject(new Error(UPDATE_APP_LINE)),
    REGISTRATION_TIMEOUT_MS
  );

  try {
    await PushNotifications.register();
    return await tokenPromise;
  } finally {
    clearTimeout(timer);
    for (const handle of handles) {
      try {
        await handle.remove();
      } catch {
        /* already gone */
      }
    }
  }
}

async function appInfo() {
  try {
    const App = plugin("App");
    const info = await withNativeTimeout(App.getInfo(), "app_info");
    return { appVersion: info?.version, appBuild: info?.build };
  } catch {
    return {};
  }
}

/**
 * Turn notifications on for this phone: the iOS permission dialog (first
 * time only), the device token, then POST /push/device. Must run from a tap,
 * like the web subscribe. Throws PushPermissionError when the user said no,
 * a plain Error (with a user-facing message) for anything else.
 *
 * @param {string} authToken - Bearer token for the current session.
 * @returns {Promise<{token: string}>}
 */
export async function registerNativePush(authToken) {
  if (!isNativePushSupported()) {
    throw new Error("App notifications are not available on this device");
  }
  if (!authToken) {
    throw new Error("Not authenticated");
  }

  const PushNotifications = pushPlugin();

  let { receive } = await withNativeTimeout(
    PushNotifications.checkPermissions(),
    "check_permissions"
  );
  if (receive === "denied") {
    cachedPermission = "denied";
    throw new PushPermissionError("denied");
  }
  if (receive !== "granted") {
    ({ receive } = await PushNotifications.requestPermissions());
  }
  cachedPermission = toWebPermission(receive);
  if (receive !== "granted") {
    throw new PushPermissionError(cachedPermission);
  }

  const token = await obtainDeviceToken(PushNotifications);
  const info = await appInfo();

  const res = await fetch(buildUrl("/push/device"), {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${authToken}`,
    },
    body: JSON.stringify({
      platform: Capacitor.getPlatform(),
      token,
      ...info,
      userAgent: navigator.userAgent,
      // This app keeps several accounts signed in (state/savedAccounts.js):
      // the login joins the phone's accounts instead of taking it over.
      multiAccount: true,
    }),
  });
  if (!res.ok) {
    throw new Error(`Device registration failed: ${res.status}`);
  }

  await saveToken(token);
  log.info("push.registered", { appVersion: info.appVersion || null });
  return { token };
}

/**
 * Tell the server to forget this phone (DELETE /push/device) and drop the
 * saved token. The OS registration itself is left alone: permission stays
 * granted, so the next login re-registers silently. Best-effort, like the
 * web unsubscribe — logout must never fail on it.
 *
 * @param {string|null} authToken - Bearer token, or null if already cleared.
 */
export async function unregisterNativePush(authToken) {
  if (!isNativePushSupported()) return;
  const token = await readSavedToken();
  if (!token) return;

  try {
    if (authToken) {
      await fetch(buildUrl("/push/device"), {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${authToken}`,
        },
        body: JSON.stringify({ token }),
      });
    }
  } catch (_) {
    /* best-effort */
  }

  await clearSavedToken();
}

/**
 * Wire the two runtime events: a push landing while the app is open, and a
 * tap on a notification (the plugin replays a tap that launched the app once
 * a listener exists, so a cold start from the lock screen lands right too).
 * Resolves with a function that removes both listeners.
 *
 * @param {{onReceived?: (notification: object) => void,
 *          onTap?: (url: string|null, notification: object) => void}} handlers
 */
export async function addNativePushListeners({ onReceived, onTap } = {}) {
  if (!isNativePushSupported()) return () => {};
  const PushNotifications = pushPlugin();
  const handles = [];

  if (onReceived) {
    handles.push(
      await PushNotifications.addListener("pushNotificationReceived", (notification) => {
        onReceived(notification);
      })
    );
  }
  if (onTap) {
    handles.push(
      await PushNotifications.addListener("pushNotificationActionPerformed", (action) => {
        const notification = action?.notification || {};
        const url = notification?.data?.url;
        const forUser = notification?.data?.forUser;
        onTap(typeof url === "string" && url.startsWith("/") ? url : null, notification, {
          forUser: typeof forUser === "string" && forUser ? forUser : null,
        });
      })
    );
  }

  return () => {
    for (const handle of handles) {
      Promise.resolve(handle.remove()).catch(() => {});
    }
  };
}
